(() => {
  const CODE_PATTERN = /^[A-Za-z0-9-]{8,40}$/;

  function init(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const form = root.querySelector('[data-tracking-form]');
    const input = root.querySelector('[data-tracking-input]');
    const submit = root.querySelector('[data-tracking-submit]');
    const message = root.querySelector('[data-tracking-message]');
    const result = root.querySelector('[data-tracking-result]');
    const loading = root.querySelector('[data-tracking-loading]');
    const steps = root.querySelector('[data-tracking-steps]');
    const supportLink = root.querySelector('[data-tracking-support]');
    let controller;

    function updateSupportLink(code = input.value.trim()) {
      const subject = root.dataset.supportSubject + (code ? `: ${code}` : '');
      const body = root.dataset.supportBody.replace('__TRACKING_CODE__', code || `[${root.dataset.supportCodeLabel}]`);
      supportLink.href = `mailto:support@astariwearables.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }

    input.addEventListener('input', () => updateSupportLink());
    updateSupportLink();

    function updateCenteredStep() {
      const center = steps.getBoundingClientRect().left + steps.clientWidth / 2;
      const items = [...steps.querySelectorAll('li')];
      const nearest = items.reduce((best, item) =>
        Math.abs(item.getBoundingClientRect().left + item.clientWidth / 2 - center) <
        Math.abs(best.getBoundingClientRect().left + best.clientWidth / 2 - center) ? item : best
      );
      items.forEach((item) => item.classList.toggle('is-centered', item === nearest));
    }

    function centerActiveStep() {
      const active = steps.querySelector('.is-active');
      if (!active) return;
      if (window.matchMedia('(max-width: 700px)').matches) {
        const container = steps.getBoundingClientRect();
        const item = active.getBoundingClientRect();
        steps.scrollTo({ left: steps.scrollLeft + item.left - container.left + item.width / 2 - container.width / 2 });
      }
      updateCenteredStep();
    }

    steps.addEventListener('scroll', updateCenteredStep, { passive: true });
    window.addEventListener('resize', centerActiveStep);

    function setText(selector, value) {
      const node = root.querySelector(selector);
      if (node) node.textContent = value || root.dataset.unavailable;
    }

    function showMessage(value) {
      message.textContent = value;
      message.hidden = !value;
    }

    function setLink(node, value) {
      let validUrl;
      try {
        validUrl = new URL(value);
      } catch {
        validUrl = null;
      }
      node.hidden = !validUrl || validUrl.protocol !== 'https:';
      if (!node.hidden) node.href = validUrl.toString();
      else node.removeAttribute('href');
    }

    function addEvent(list, event, carrierLink) {
      const item = document.createElement('li');
      const marker = document.createElement('span');
      marker.className = 'astari-tracking__event-marker';
      marker.setAttribute('aria-hidden', 'true');
      const body = document.createElement('div');
      body.className = 'astari-tracking__event-body';
      const top = document.createElement('div');
      top.className = 'astari-tracking__event-top';
      const heading = document.createElement('strong');
      const date = document.createElement('time');
      const location = document.createElement('p');
      heading.textContent = event.event || root.dataset.unavailable;
      date.textContent = [event.date, event.time].filter(Boolean).join(' ');
      location.textContent = event.location || root.dataset.unknownLocation;
      top.append(heading);
      if (date.textContent) top.append(date);
      body.append(top, location);
      if (event.comments) {
        const comment = document.createElement('small');
        comment.textContent = event.comments;
        if (carrierLink) {
          const row = document.createElement('div');
          row.className = 'astari-tracking__carrier-row';
          const link = carrierLink.cloneNode(true);
          link.removeAttribute('data-tracking-carrier-summary');
          row.append(comment, link);
          body.append(row);
        } else body.append(comment);
      }
      item.append(marker, body);
      list.append(item);
    }

    function render(data) {
      setText('[data-tracking-number]', data.trackingNumber);
      setText('[data-tracking-route]', [data.destination || root.dataset.unknownDestination, data.carrier].filter(Boolean).join(' · '));
      setText('[data-tracking-latest-event]', data.latest?.event || data.status);
      setText('[data-tracking-status]', data.status);
      setText('[data-tracking-datetime]', [data.latest?.date, data.latest?.time].filter(Boolean).join(' · '));
      setText('[data-tracking-location]', data.latest?.location);
      const royalMailWarning = root.dataset.royalMailWarningEnabled === 'true' &&
        typeof data.destination === 'string' && data.destination.trim().toUpperCase() === 'UNITED KINGDOM';
      root.querySelector('[data-tracking-royal-mail-warning]').hidden = !royalMailWarning;

      const current = data.pending ? 0 : Math.max(0, Math.min(3, Number(data.step) || 0));
      steps.style.setProperty('--progress-percent', `${current / 3 * 100}%`);
      steps.querySelectorAll('li').forEach((step, index) => {
        step.classList.toggle('is-done', index < current);
        step.classList.toggle('is-active', index === current);
        if (index === current) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
      requestAnimationFrame(centerActiveStep);

      root.querySelector('[data-tracking-pending]').hidden = !data.pending;
      const carrierLink = root.querySelector('[data-tracking-carrier-summary]');
      setLink(carrierLink, royalMailWarning ? null : data.localCarrierUrl);

      const list = root.querySelector('[data-tracking-events]');
      list.replaceChildren();
      if (!carrierLink.hidden) {
        addEvent(list, {
          event: root.dataset.localCarrierEvent,
          location: data.latest?.location || root.dataset.unknownLocation,
          comments: root.dataset.localCarrierNote,
        }, carrierLink);
      }
      (Array.isArray(data.events) ? data.events : []).forEach((event) => addEvent(list, event));

      updateSupportLink(data.trackingNumber || input.value.trim());
      result.hidden = false;
      root.classList.add('is-tracking');
      root.classList.remove('is-editing');
    }

    async function lookup(code, updateUrl) {
      controller?.abort();
      controller = null;
      const normalized = code.trim();
      input.value = normalized;
      updateSupportLink(normalized);
      if (!CODE_PATTERN.test(normalized)) {
        root.classList.add('is-editing');
        root.classList.remove('is-loading');
        submit.disabled = false;
        loading.hidden = true;
        result.hidden = true;
        showMessage(root.dataset.invalid);
        input.focus();
        return;
      }

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.delete('tn');
        url.searchParams.delete('order');
        url.searchParams.set('code', normalized);
        window.history.pushState({}, '', url);
      }

      controller = new AbortController();
      const request = controller;
      root.classList.add('is-tracking');
      root.classList.add('is-loading');
      root.classList.remove('is-editing');
      submit.disabled = true;
      result.hidden = true;
      loading.hidden = false;
      showMessage(root.dataset.loading);

      try {
        const url = new URL(root.dataset.endpoint);
        url.searchParams.set('code', normalized);
        const response = await fetch(url, { signal: request.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Tracking request failed');
        const data = await response.json();
        if (request !== controller) return;
        render(data);
        root.classList.remove('is-loading');
        loading.hidden = true;
        showMessage('');
      } catch (error) {
        if (error.name === 'AbortError' || request !== controller) return;
        root.classList.remove('is-loading');
        root.classList.add('is-editing');
        loading.hidden = true;
        showMessage(root.dataset.error);
      } finally {
        if (request === controller) submit.disabled = false;
      }
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      lookup(input.value, true);
    });

    const onPopState = () => {
      if (!root.isConnected) return;
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code') || params.get('tn') || params.get('order');
      if (code) lookup(code, false);
      else {
        controller?.abort();
        controller = null;
        root.classList.remove('is-tracking', 'is-editing', 'is-loading');
        input.value = '';
        updateSupportLink();
        result.hidden = true;
        loading.hidden = true;
        showMessage('');
        submit.disabled = false;
      }
    };
    window.addEventListener('popstate', onPopState);

    const onSectionUnload = (event) => {
      if (!event.target.contains(root)) return;
      controller?.abort();
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('resize', centerActiveStep);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    };
    document.addEventListener('shopify:section:unload', onSectionUnload);

    const params = new URLSearchParams(window.location.search);
    const initialCode = params.get('code') || params.get('tn') || params.get('order');
    if (initialCode) lookup(initialCode, false);
  }

  function initAll(container = document) {
    container.querySelectorAll('[data-astari-tracking]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
